import { useCallback, useRef, useState } from 'react';
import { FormatListBulletedOutlined } from '@mui/icons-material';
import {
	ConfirmModal,
	FormActions,
	FormCard,
	FormSectionTitle,
	PageHeader,
	useConfirmModal,
	useToast,
} from '@practium/ui';
import type { FormPageMode } from '@practium/ui';
import type { UserPageMode, SystemUserFormData } from '../types';
import { UserForm } from '../components/Forms/UserForm/UserForm';
import type { UserFormRef } from '../components/Forms/UserForm/UserForm';
import { AvatarCard } from '../components/AvatarCard/AvatarCard';
import { USER_TITLES, USER_SUBTITLES, USER_SECTION_TITLE } from '../constants/page.constants';
import styles from './AddUserPage.module.css';
import { useDeleteuser } from '@practium/features/api';

export interface AddUserPageProps {
	onBack: () => void;
	initialMode?: UserPageMode;
	initialData?: SystemUserFormData;
	onDelete?: () => void;
	canEdit?: boolean;
	id: string;
	avatarUrl?: string;
}

export const AddUserPage = ({
	onBack,
	initialMode = 'add',
	initialData,
	onDelete,
	canEdit = true,
	id,
	avatarUrl,
}: AddUserPageProps) => {
	const [currentMode, setCurrentMode] = useState<UserPageMode>(initialMode);
	const [isLoading, setIsLoading] = useState(false);
	const [avatarFile, setAvatarFile] = useState<File | null>(null);

	const userRef = useRef<UserFormRef>(null);

	const { mutateAsync: deleteUser } = useDeleteuser();

	const {
		confirm,
		isOpen: isConfirmOpen,
		options: confirmOptions,
		handleConfirm,
		handleCancel: handleConfirmCancel,
	} = useConfirmModal();

	const { success: toastSuccess, error: toastError } = useToast();

	const handleSubmit = useCallback(() => {
		onBack();
	}, [onBack]);

	const handleCancel = useCallback(async () => {
		const revert = () => {
			setAvatarFile(null);
			if (currentMode === 'edit' && initialMode === 'view') {
				setCurrentMode('view');
			} else {
				onBack();
			}
		};

		if (!avatarFile && !userRef.current?.isDirty?.()) {
			revert();
			return;
		}

		const confirmed = await confirm({
			title: 'Cancelar alterações',
			message: 'Deseja descartar as alterações não salvas?',
			confirmText: 'Descartar',
			cancelText: 'Continuar editando',
			variant: 'warning',
		});
		if (!confirmed) return;
		revert();
	}, [avatarFile, confirm, currentMode, initialMode, onBack]);

	const handleDelete = useCallback(async () => {
		const confirmed = await confirm({
			title: 'Excluir cadastro',
			message: 'Tem certeza que deseja excluir este cadastro? Esta ação não pode ser desfeita.',
			confirmText: 'Excluir',
			cancelText: 'Cancelar',
			variant: 'danger',
		});
		if (!confirmed) return;
		setIsLoading(true);
		try {
			await deleteUser(id);
			toastSuccess('Cadastro excluído com sucesso.');
			onDelete?.();
		} catch (err) {
			toastError(err instanceof Error ? err.message : 'Erro ao excluir cadastro.');
		} finally {
			setIsLoading(false);
		}
	}, [confirm, deleteUser, id, onDelete, toastSuccess, toastError]);

	const handleEdit = useCallback(() => setCurrentMode('edit'), []);

	const handleSave = useCallback(() => {
		userRef.current?.submit();
	}, []);

	const formMode: FormPageMode = currentMode;

	const title = USER_TITLES[currentMode];
	const subtitle = USER_SUBTITLES[currentMode];

	const pageActions = (
		<div className={styles.pageActions}>
			<FormActions
				mode={formMode}
				onCancel={handleCancel}
				onSave={handleSave}
				isLoading={isLoading}
				onDelete={onDelete ? handleDelete : undefined}
				onEdit={canEdit ? handleEdit : undefined}
			/>
		</div>
	);

	const avatarCard = (
		<AvatarCard
			mode={currentMode}
			file={avatarFile}
			onFileChange={setAvatarFile}
			personName={
				initialData ? `${initialData.name ?? ''} ${initialData.surName ?? ''}`.trim() : undefined
			}
			avatarUrl={avatarUrl}
			onEditRequest={canEdit ? handleEdit : undefined}
		/>
	);

	return (
		<div className={styles.page}>
			<PageHeader title={title} subtitle={subtitle} onBack={onBack} />
			<div className={styles.cards}>
				<div className={styles.mainContent}>
					<FormCard
						icon={<FormatListBulletedOutlined />}
						title="Informações Pessoais"
						className={styles.mainCard}
					>
						<div className={styles.formContent}>
							<FormSectionTitle>{USER_SECTION_TITLE}</FormSectionTitle>
							<div className={styles.fields}>
								<UserForm
									ref={userRef}
									mode={currentMode}
									initialData={initialData}
									onSubmit={handleSubmit}
									onLoadingChange={setIsLoading}
								/>
							</div>
						</div>
					</FormCard>
					{pageActions}
				</div>
				{avatarCard}
			</div>

			<ConfirmModal
				isOpen={isConfirmOpen}
				title={confirmOptions.title}
				message={confirmOptions.message}
				confirmText={confirmOptions.confirmText}
				cancelText={confirmOptions.cancelText}
				variant={confirmOptions.variant}
				onConfirm={handleConfirm}
				onCancel={handleConfirmCancel}
			/>
		</div>
	);
};
